import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Send, CheckCircle2, RotateCcw } from "lucide-react";
import { api } from "@/lib/api";
import { PageTitle } from "./AdminAnalytics";

const fmt = (ts) => {
  const d = new Date(ts);
  const today = d.toDateString() === new Date().toDateString();
  return today ? d.toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" }) : d.toLocaleDateString("it-IT", { day: "2-digit", month: "short" });
};
const PAGES = { "/": "Landing", "/test": "Beauty Test", "/analisi": "Analisi", "/profilo": "Skin Profile", "/routine": "Routine" };

export default function AdminChat() {
  const [params, setParams] = useSearchParams();
  const [convs, setConvs] = useState([]);
  const selected = params.get("s");

  useEffect(() => {
    const loadConvs = () => api.get("/admin/chat/conversations").then((r) => setConvs(r.data)).catch(() => {});
    loadConvs();
    const t = setInterval(loadConvs, 4000);
    return () => clearInterval(t);
  }, []);

  return (
    <div data-testid="admin-chat-page" className="h-[calc(100vh-7rem)] flex flex-col">
      <PageTitle eyebrow="Assistenza" title="Chat live" />
      <div className="flex-1 min-h-0 grid md:grid-cols-[300px_1fr] bg-white border border-[#E2DACF]">
        <aside className={`border-r border-[#E2DACF] overflow-y-auto ${selected ? "hidden md:block" : ""}`} data-testid="chat-conversation-list">
          {!convs.length && <p className="p-5 text-sm text-[#C2B8A8]">Nessuna conversazione ancora.</p>}
          {convs.map((c) => (
            <button key={c.session_id} data-testid={`chat-conv-${c.session_id}`} onClick={() => setParams({ s: c.session_id })}
              className={`w-full text-left px-4 py-3.5 border-b border-[#F2ECE4] transition-colors hover:bg-[#FAF8F5] ${selected === c.session_id ? "bg-[#F5F3EF]" : ""}`}>
              <div className="flex items-center justify-between gap-2">
                <p className="text-sm font-medium truncate">{c.customer_name || "Cliente"} <span className="text-[#C2B8A8] font-normal">· {PAGES[c.page] || c.page}</span></p>
                <span className="text-[11px] text-[#8C8275] shrink-0">{fmt(c.last_at)}</span>
              </div>
              <div className="flex items-center justify-between gap-2 mt-1">
                <p className="text-xs text-[#8C8275] truncate">{c.last_sender === "admin" ? "Tu: " : ""}{c.last_message}</p>
                {c.unread_admin > 0 && <span data-testid="chat-conv-unread" className="min-w-[20px] h-5 px-1 rounded-full bg-[#C5A059] text-[11px] flex items-center justify-center shrink-0">{c.unread_admin}</span>}
                {c.status === "closed" && !c.unread_admin && <CheckCircle2 size={14} className="text-[#C2B8A8] shrink-0" />}
              </div>
            </button>
          ))}
        </aside>
        {selected ? <Thread sessionId={selected} onBack={() => setParams({})} /> : (
          <div className="hidden md:flex items-center justify-center text-sm text-[#C2B8A8]">Seleziona una conversazione</div>
        )}
      </div>
    </div>
  );
}

const Thread = ({ sessionId, onBack }) => {
  const [data, setData] = useState(null);
  const [text, setText] = useState("");
  const endRef = useRef(null);

  const load = useCallback(() => api.get(`/admin/chat/${sessionId}/messages`).then((r) => setData(r.data)).catch(() => {}), [sessionId]);
  useEffect(() => { setData(null); load(); const t = setInterval(load, 3000); return () => clearInterval(t); }, [load]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [data?.messages?.length]);

  const send = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    await api.post(`/admin/chat/${sessionId}/messages`, { text });
    setText("");
    load();
  };
  const toggle = async () => {
    await api.put(`/admin/chat/${sessionId}/status`, { status: conv.status === "closed" ? "open" : "closed" });
    load();
  };

  if (!data) return <div className="p-6 text-sm text-[#8C8275]">Caricamento…</div>;
  const conv = data.conversation || {};
  const skin = data.customer?.profile?.skin_type?.name;

  return (
    <div className="flex flex-col min-h-0" data-testid="chat-thread">
      <div className="flex items-center justify-between gap-3 px-5 py-3 border-b border-[#E2DACF]">
        <div className="min-w-0">
          <button onClick={onBack} className="md:hidden text-xs text-[#8C8275] mb-1" data-testid="chat-thread-back">← Conversazioni</button>
          <p className="font-serif text-lg truncate" data-testid="chat-thread-name">{conv.customer_name || "Cliente"}</p>
          <p className="text-xs text-[#8C8275]">{PAGES[conv.page] || conv.page}{skin ? ` · ${skin}` : ""}{data.customer?.utm?.utm_source ? ` · da ${data.customer.utm.utm_source}` : ""}</p>
        </div>
        <button data-testid="chat-thread-toggle-status" onClick={toggle} className="fs-btn-ghost !py-2 !px-3 text-xs shrink-0">
          {conv.status === "closed" ? <><RotateCcw size={13} /> Riapri</> : <><CheckCircle2 size={13} /> Chiudi</>}
        </button>
      </div>
      <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3 bg-[#FAF8F5]" data-testid="chat-thread-messages">
        {data.messages.map((m) => (
          <div key={m.id} className={`flex ${m.sender === "admin" ? "justify-end" : "justify-start"}`} data-testid={`admin-bubble-${m.sender}`}>
            <div className={`max-w-[75%] px-4 py-2.5 text-sm leading-relaxed ${m.sender === "admin" ? "bg-[#1A1A18] text-[#FAF8F5] rounded-2xl rounded-br-sm" : "bg-white border border-[#E6DFD5] rounded-2xl rounded-bl-sm"}`}>
              <p className="whitespace-pre-wrap break-words">{m.text}</p>
              <p className={`text-[10px] mt-1 ${m.sender === "admin" ? "text-white/50" : "text-[#8C8275]"}`}>{fmt(m.ts)}</p>
            </div>
          </div>
        ))}
        <div ref={endRef} />
      </div>
      <form onSubmit={send} className="flex gap-2 p-3 border-t border-[#E2DACF]">
        <input data-testid="admin-chat-input" value={text} onChange={(e) => setText(e.target.value)} placeholder="Rispondi al cliente…"
          className="flex-1 text-sm px-3 py-2.5 bg-[#FAF8F5] border border-[#E6DFD5] outline-none focus:border-[#C5A059]" />
        <button data-testid="admin-chat-send-btn" type="submit" disabled={!text.trim()} className="fs-btn !px-4"><Send size={16} strokeWidth={1.5} /></button>
      </form>
    </div>
  );
};
